import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { TagInput } from './TagInput';
import { CurrencyInput } from './CurrencyInput';
import type { JobIntelUserProfile } from '@/types/jobintelProfile';
import {
  EXPERIENCE_LEVEL_LABELS, EDUCATION_LEVEL_LABELS, REMOTE_PREFERENCE_LABELS,
  AVAILABILITY_LABELS, COMPANY_SIZE_LABELS, COMPANY_STRUCTURE_LABELS,
  EMPLOYMENT_TYPE_LABELS, SENIORITY_LEVEL_LABELS,
} from '@/types/jobintelProfile';
import { calculateCompletionPercentage } from '@/services/jobintelProfileService';

interface Props {
  initialData: Partial<JobIntelUserProfile>;
  onComplete: (data: Partial<JobIntelUserProfile>) => void;
  onBack: () => void;
  fieldMeta?: Record<string, { confidence: string; source?: string }>;
}

const TOTAL_STEPS = 8;

export function ManualProfileForm({ initialData, onComplete, onBack, fieldMeta }: Props) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<Partial<JobIntelUserProfile>>(initialData);

  const update = (field: string, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const completion = calculateCompletionPercentage(form);

  const renderFieldIndicator = (field: string) => {
    if (!fieldMeta?.[field]) return null;
    const meta = fieldMeta[field];
    if (meta.confidence === 'extracted') {
      return <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--success))]"><Check className="h-3 w-3" /> Found in your documents</span>;
    }
    if (meta.confidence === 'inferred') {
      return <span className="text-xs text-[hsl(var(--warning))]">⚡ Inferred — please verify</span>;
    }
    return <span className="text-xs text-[hsl(var(--destructive))]">● Needs your input</span>;
  };

  const renderSelectOptions = (labels: Record<string, string>) =>
    Object.entries(labels).map(([value, label]) => (
      <SelectItem key={value} value={value}>{label}</SelectItem>
    ));

  const renderStep = () => {
    switch (step) {
      case 1: return (
        <div className="space-y-5">
          <div className="space-y-2">
            <Label>What is your current job title?</Label>
            {renderFieldIndicator('current_job_title')}
            <Input value={form.current_job_title || ''} onChange={e => update('current_job_title', e.target.value)} placeholder="e.g. Senior Product Manager" />
          </div>
          <div className="space-y-2">
            <Label>What industry do you work in?</Label>
            {renderFieldIndicator('current_industry')}
            <Input value={form.current_industry || ''} onChange={e => update('current_industry', e.target.value)} placeholder="e.g. Technology, Healthcare" />
          </div>
          <div className="space-y-2">
            <Label>How many years of professional experience do you have?</Label>
            {renderFieldIndicator('years_of_experience')}
            <Input type="number" value={form.years_of_experience ?? ''} onChange={e => update('years_of_experience', e.target.value ? parseInt(e.target.value) : null)} placeholder="e.g. 12" />
          </div>
          <div className="space-y-2">
            <Label>What best describes your experience level?</Label>
            {renderFieldIndicator('experience_level')}
            <Select value={form.experience_level || ''} onValueChange={v => update('experience_level', v)}>
              <SelectTrigger><SelectValue placeholder="Select level" /></SelectTrigger>
              <SelectContent>{renderSelectOptions(EXPERIENCE_LEVEL_LABELS)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>What is your highest education level?</Label>
            {renderFieldIndicator('education_level')}
            <Select value={form.education_level || ''} onValueChange={v => update('education_level', v)}>
              <SelectTrigger><SelectValue placeholder="Select education" /></SelectTrigger>
              <SelectContent>{renderSelectOptions(EDUCATION_LEVEL_LABELS)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>What did you study?</Label>
            {renderFieldIndicator('education_field')}
            <Input value={form.education_field || ''} onChange={e => update('education_field', e.target.value)} placeholder="e.g. Computer Science, Business Administration" />
          </div>
        </div>
      );

      case 2: return (
        <div className="space-y-5">
          <div className="space-y-2">
            <Label>What are your core skills?</Label>
            {renderFieldIndicator('core_skills')}
            <TagInput value={form.core_skills || []} onChange={v => update('core_skills', v)} placeholder="Add a skill and press Enter" />
          </div>
          <div className="space-y-2">
            <Label>What are your technical skills?</Label>
            {renderFieldIndicator('technical_skills')}
            <TagInput value={form.technical_skills || []} onChange={v => update('technical_skills', v)} placeholder="Add a technical skill" />
          </div>
          <div className="space-y-2">
            <Label>What are your leadership skills?</Label>
            {renderFieldIndicator('leadership_skills')}
            <TagInput value={form.leadership_skills || []} onChange={v => update('leadership_skills', v)} placeholder="Add a leadership skill" />
          </div>
          <div className="space-y-2">
            <Label>What certifications do you hold?</Label>
            {renderFieldIndicator('certifications')}
            <TagInput value={form.certifications || []} onChange={v => update('certifications', v)} placeholder="e.g. PMP, AWS Solutions Architect" />
          </div>
        </div>
      );

      case 3: return (
        <div className="space-y-5">
          <div className="space-y-2">
            <Label>What job titles are you going after? (up to 5)</Label>
            {renderFieldIndicator('target_job_titles')}
            <TagInput value={form.target_job_titles || []} onChange={v => update('target_job_titles', v)} maxTags={5} placeholder="e.g. VP of Product" />
          </div>
          <div className="space-y-2">
            <Label>Any alternate titles you'd also consider?</Label>
            {renderFieldIndicator('target_job_title_variations')}
            <TagInput value={form.target_job_title_variations || []} onChange={v => update('target_job_title_variations', v)} placeholder="e.g. Head of Product, Director of PM" />
          </div>
          <div className="space-y-2">
            <Label>What seniority level are you targeting?</Label>
            {renderFieldIndicator('target_seniority_level')}
            <Select value={form.target_seniority_level || ''} onValueChange={v => update('target_seniority_level', v)}>
              <SelectTrigger><SelectValue placeholder="Select seniority" /></SelectTrigger>
              <SelectContent>{renderSelectOptions(SENIORITY_LEVEL_LABELS)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>What type of employment are you looking for?</Label>
            {renderFieldIndicator('employment_type')}
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(EMPLOYMENT_TYPE_LABELS).map(([value, label]) => (
                <label key={value} className="flex items-center gap-2 p-2 rounded border cursor-pointer hover:bg-muted/50">
                  <input
                    type="checkbox"
                    checked={(form.employment_type || []).includes(value)}
                    onChange={(e) => {
                      const current = form.employment_type || [];
                      update('employment_type', e.target.checked ? [...current, value] : current.filter(v => v !== value));
                    }}
                    className="rounded"
                  />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      );

      case 4: return (
        <div className="space-y-5">
          <div className="space-y-2">
            <Label>What industries are you targeting?</Label>
            {renderFieldIndicator('target_industries')}
            <TagInput value={form.target_industries || []} onChange={v => update('target_industries', v)} placeholder="e.g. SaaS, FinTech" />
          </div>
          <div className="space-y-2">
            <Label>Any industries you want to exclude?</Label>
            <TagInput value={form.excluded_industries || []} onChange={v => update('excluded_industries', v)} placeholder="e.g. Oil & Gas" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Current city</Label>
              {renderFieldIndicator('current_city')}
              <Input value={form.current_city || ''} onChange={e => update('current_city', e.target.value)} placeholder="e.g. Austin" />
            </div>
            <div className="space-y-2">
              <Label>Current state</Label>
              {renderFieldIndicator('current_state')}
              <Input value={form.current_state || ''} onChange={e => update('current_state', e.target.value)} placeholder="e.g. TX" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>ZIP code</Label>
            {renderFieldIndicator('current_zip')}
            <Input value={form.current_zip || ''} onChange={e => update('current_zip', e.target.value)} placeholder="e.g. 78701" />
          </div>
          <div className="space-y-2">
            <Label>What's your remote work preference?</Label>
            {renderFieldIndicator('remote_preference')}
            <Select value={form.remote_preference || ''} onValueChange={v => update('remote_preference', v)}>
              <SelectTrigger><SelectValue placeholder="Select preference" /></SelectTrigger>
              <SelectContent>{renderSelectOptions(REMOTE_PREFERENCE_LABELS)}</SelectContent>
            </Select>
          </div>
          {form.remote_preference === 'hybrid_preferred' && (
            <div className="space-y-2">
              <Label>How many days in-office is acceptable?</Label>
              <Input type="number" value={form.hybrid_days_acceptable ?? ''} onChange={e => update('hybrid_days_acceptable', e.target.value ? parseInt(e.target.value) : null)} placeholder="e.g. 2" />
            </div>
          )}
          <div className="flex items-center gap-3">
            <Switch checked={form.open_to_relocation || false} onCheckedChange={v => update('open_to_relocation', v)} />
            <Label>Open to relocation</Label>
          </div>
          {form.open_to_relocation && (
            <div className="space-y-2">
              <Label>Which cities would you relocate to?</Label>
              <TagInput value={form.relocation_cities || []} onChange={v => update('relocation_cities', v)} placeholder="e.g. San Francisco, CA" />
            </div>
          )}
          <div className="flex items-center gap-3">
            <Switch checked={form.willing_to_travel || false} onCheckedChange={v => update('willing_to_travel', v)} />
            <Label>Willing to travel</Label>
          </div>
          {form.willing_to_travel && (
            <div className="space-y-2">
              <Label>Maximum travel percentage</Label>
              <Input type="number" value={form.max_travel_percentage ?? ''} onChange={e => update('max_travel_percentage', e.target.value ? parseInt(e.target.value) : null)} placeholder="e.g. 25" />
            </div>
          )}
        </div>
      );

      case 5: return (
        <div className="space-y-5">
          <div className="space-y-2">
            <Label>What is the lowest salary you would accept?</Label>
            {renderFieldIndicator('salary_minimum_cents')}
            <CurrencyInput valueCents={form.salary_minimum_cents} onChange={v => update('salary_minimum_cents', v)} placeholder="$80,000" />
          </div>
          <div className="space-y-2">
            <Label>What is your target salary?</Label>
            {renderFieldIndicator('salary_target_cents')}
            <CurrencyInput valueCents={form.salary_target_cents} onChange={v => update('salary_target_cents', v)} placeholder="$120,000" />
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.open_to_variable_comp || false} onCheckedChange={v => update('open_to_variable_comp', v)} />
            <Label>Open to variable compensation (bonuses, commissions)</Label>
          </div>
          {form.open_to_variable_comp && (
            <div className="space-y-2">
              <Label>Minimum base salary if variable comp is included?</Label>
              <CurrencyInput valueCents={form.variable_comp_minimum_base_cents} onChange={v => update('variable_comp_minimum_base_cents', v)} />
            </div>
          )}
          <div className="flex items-center gap-3">
            <Switch checked={form.interested_in_equity || false} onCheckedChange={v => update('interested_in_equity', v)} />
            <Label>Interested in equity / stock options</Label>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.currently_employed || false} onCheckedChange={v => update('currently_employed', v)} />
            <Label>Currently employed</Label>
          </div>
          <div className="space-y-2">
            <Label>When could you start a new role?</Label>
            {renderFieldIndicator('availability')}
            <Select value={form.availability || ''} onValueChange={v => update('availability', v)}>
              <SelectTrigger><SelectValue placeholder="Select availability" /></SelectTrigger>
              <SelectContent>{renderSelectOptions(AVAILABILITY_LABELS)}</SelectContent>
            </Select>
          </div>
        </div>
      );

      case 6: return (
        <div className="space-y-5">
          <div className="space-y-2">
            <Label>What size companies do you prefer?</Label>
            {renderFieldIndicator('preferred_company_sizes')}
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(COMPANY_SIZE_LABELS).map(([value, label]) => (
                <label key={value} className="flex items-center gap-2 p-2 rounded border cursor-pointer hover:bg-muted/50">
                  <input
                    type="checkbox"
                    checked={(form.preferred_company_sizes || []).includes(value)}
                    onChange={(e) => {
                      const current = form.preferred_company_sizes || [];
                      update('preferred_company_sizes', e.target.checked ? [...current, value] : current.filter(v => v !== value));
                    }}
                    className="rounded"
                  />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>What type of company structure?</Label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(COMPANY_STRUCTURE_LABELS).map(([value, label]) => (
                <label key={value} className="flex items-center gap-2 p-2 rounded border cursor-pointer hover:bg-muted/50">
                  <input
                    type="checkbox"
                    checked={(form.preferred_company_structure || []).includes(value)}
                    onChange={(e) => {
                      const current = form.preferred_company_structure || [];
                      update('preferred_company_structure', e.target.checked ? [...current, value] : current.filter(v => v !== value));
                    }}
                    className="rounded"
                  />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Companies you're actively pursuing</Label>
            <TagInput value={form.target_companies || []} onChange={v => update('target_companies', v)} placeholder="e.g. Google, Stripe" />
          </div>
          <div className="space-y-2">
            <Label>Companies to never show in alerts</Label>
            <TagInput value={form.excluded_companies || []} onChange={v => update('excluded_companies', v)} placeholder="e.g. Previous employer" />
          </div>
        </div>
      );

      case 7: return (
        <div className="space-y-5">
          <div className="space-y-2">
            <Label>Keywords that must appear in a job posting</Label>
            <TagInput value={form.required_keywords || []} onChange={v => update('required_keywords', v)} placeholder="e.g. remote, product-led" />
          </div>
          <div className="space-y-2">
            <Label>Keywords that disqualify a posting</Label>
            <TagInput value={form.excluded_keywords || []} onChange={v => update('excluded_keywords', v)} placeholder="e.g. entry-level, commission-only" />
          </div>
          <div className="space-y-2">
            <Label>What benefits are required for you?</Label>
            <TagInput value={form.required_benefits || []} onChange={v => update('required_benefits', v)} placeholder="e.g. 401k match, health insurance" />
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.requires_visa_sponsorship || false} onCheckedChange={v => update('requires_visa_sponsorship', v)} />
            <Label>Requires visa sponsorship</Label>
          </div>
          <div className="space-y-2">
            <Label>Any work schedule requirements?</Label>
            <Input value={form.work_schedule_requirements || ''} onChange={e => update('work_schedule_requirements', e.target.value)} placeholder="e.g. No weekends, flexible hours" />
          </div>
          <div className="space-y-2">
            <Label>Anything else that would make you decline an opportunity?</Label>
            <Textarea value={form.deal_breakers || ''} onChange={e => update('deal_breakers', e.target.value)} placeholder="Describe your absolute deal breakers..." rows={3} />
          </div>
        </div>
      );

      case 8: return (
        <ProfileReviewSummary form={form} onEditStep={setStep} />
      );

      default: return null;
    }
  };

  const stepTitles = [
    'Your Background',
    'Your Skills & Expertise',
    'What You\'re Looking For',
    'Where You Want to Work',
    'Compensation',
    'Company Preferences',
    'Deal Breakers & Requirements',
    'Review & Confirm',
  ];

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Progress */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="font-medium text-foreground">Step {step} of {TOTAL_STEPS}: {stepTitles[step - 1]}</span>
          <span className="text-primary font-bold">{completion}% Complete</span>
        </div>
        <Progress value={(step / TOTAL_STEPS) * 100} />
      </div>

      {/* Form Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">{stepTitles[step - 1]}</CardTitle>
        </CardHeader>
        <CardContent>{renderStep()}</CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={step === 1 ? onBack : () => setStep(s => s - 1)}>
          <ChevronLeft className="h-4 w-4 mr-1" />
          {step === 1 ? 'Back to Setup' : 'Previous'}
        </Button>
        {step < TOTAL_STEPS ? (
          <Button onClick={() => setStep(s => s + 1)}>
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={() => onComplete(form)} className="bg-[hsl(var(--primary))]">
            Save My Profile
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Review Summary Sub-component ──

function ProfileReviewSummary({ form, onEditStep }: { form: Partial<JobIntelUserProfile>; onEditStep: (step: number) => void }) {
  const section = (title: string, stepNum: number, items: [string, any][]) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground">{title}</h3>
        <Button variant="ghost" size="sm" onClick={() => onEditStep(stepNum)} className="text-primary text-xs">Edit</Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
        {items.filter(([, v]) => v != null && v !== '' && !(Array.isArray(v) && v.length === 0)).map(([label, value]) => (
          <div key={label} className="flex gap-2">
            <span className="text-muted-foreground">{label}:</span>
            <span className="text-foreground font-medium">
              {Array.isArray(value) ? value.join(', ') : typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  const formatCurrency = (cents?: number | null) => cents ? `$${(cents / 100).toLocaleString()}` : null;

  return (
    <div className="space-y-6 divide-y">
      {section('Background', 1, [
        ['Title', form.current_job_title], ['Industry', form.current_industry],
        ['Experience', form.years_of_experience ? `${form.years_of_experience} years` : null],
        ['Level', form.experience_level], ['Education', form.education_level], ['Field', form.education_field],
      ])}
      {section('Skills', 2, [
        ['Core', form.core_skills], ['Technical', form.technical_skills],
        ['Leadership', form.leadership_skills], ['Certifications', form.certifications],
      ])}
      {section('Target Roles', 3, [
        ['Titles', form.target_job_titles], ['Variations', form.target_job_title_variations],
        ['Seniority', form.target_seniority_level], ['Employment', form.employment_type],
      ])}
      {section('Location', 4, [
        ['City', form.current_city], ['State', form.current_state], ['ZIP', form.current_zip],
        ['Remote', form.remote_preference], ['Relocation', form.open_to_relocation],
        ['Relocation Cities', form.relocation_cities], ['Travel', form.willing_to_travel],
      ])}
      {section('Compensation', 5, [
        ['Minimum', formatCurrency(form.salary_minimum_cents)], ['Target', formatCurrency(form.salary_target_cents)],
        ['Variable Comp', form.open_to_variable_comp], ['Equity', form.interested_in_equity],
        ['Availability', form.availability],
      ])}
      {section('Company', 6, [
        ['Sizes', form.preferred_company_sizes], ['Structure', form.preferred_company_structure],
        ['Target', form.target_companies], ['Excluded', form.excluded_companies],
      ])}
      {section('Requirements', 7, [
        ['Required Keywords', form.required_keywords], ['Excluded Keywords', form.excluded_keywords],
        ['Benefits', form.required_benefits], ['Visa', form.requires_visa_sponsorship],
        ['Deal Breakers', form.deal_breakers],
      ])}
    </div>
  );
}
